import React, { useState, useEffect } from "react";
import { Box, Stepper, Step, StepLabel, Button } from "@mui/material";
import { useSelector } from "react-redux";
import RecordedContactInfoStep from "./RecordedContactInfoStep";
import RecordedContactEndpointStep from "./RecordedContactEndpointStep";
import RecordedContactRetentionStep from "./RecordedContactRetentionStep";
import {
  steps,
  fields,
  channelOptions,
  retentionPolicies,
} from "./RecordedContactFormConfig";

// Generate a unique ID for ephemeral rows
const generateId = () => Math.random().toString(36).substring(2, 9);

function RecordedContactForm({
  onSubmit,
  activeStep,
  setActiveStep,
  initialData = {},
}) {
  const [formData, setFormData] = useState({ endpoints: [] });
  const [formErrors, setFormErrors] = useState({});
  const [ephemeralRows, setEphemeralRows] = useState([
    { id: generateId(), channel: "", identifier: "", added: false },
  ]);

  // Hydrate initial data for edit mode
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const transformedEndpoints = initialData.endpoints
        ? initialData.endpoints.map((ep) => ({
            id: ep.id,
            channel: ep.channel,
            identifier: ep.identifier,
            added: true,
          }))
        : [];
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        endpoints: transformedEndpoints,
        retentionPolicy: initialData.retentionPolicy?.name || "",
      }));
      setEphemeralRows(
        transformedEndpoints.length > 0
          ? [
              ...transformedEndpoints,
              { id: generateId(), channel: "", identifier: "", added: false },
            ]
          : ephemeralRows
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supervisors = useSelector((state) => state.users.users);
  const loadingUsers = useSelector((state) => state.users.loading);
  const supervisorOptions = supervisors.map((user) => ({
    label: user.fullName,
    value: user.id,
  }));

  // Validation logic per step
  const validateCurrentStep = () => {
    const errors = {};

    // STEP 0: Validate required contact info fields
    if (activeStep === 0) {
      fields.forEach((field) => {
        if (field.validation === "required") {
          const value = formData[field.key];
          if (!value) {
            errors[field.key] = `${field.name} is required`;
          }
        }
      });
    }

    // STEP 1: Validate that at least one endpoint is added
    if (activeStep === 1) {
      if (formData.endpoints.length === 0) {
        errors.endpoints = "Please add at least one endpoint. After entering the channel and identifier, click the + button to add it.";
      }
    }

    // STEP 2: Validate retention policy selection
    if (activeStep === 2) {
      if (!formData.retentionPolicy) {
        errors.retentionPolicy = "Please select a retention policy.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setFormErrors({});
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleFinalSubmit = () => {
    if (!validateCurrentStep()) return;
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handlers for ephemeral endpoint rows
  const handleEphemeralRowChange = (rowIndex, field, value) => {
    setEphemeralRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row))
    );
  };

  const handleAddEndpoint = (rowIndex) => {
    const row = ephemeralRows[rowIndex];
    const { channel, identifier, id } = row;
    if (!channel || !identifier) {
      alert("Please select a channel and enter an identifier first.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      endpoints: [...prev.endpoints, { id, channel, identifier }],
    }));
    setEphemeralRows((prev) =>
      prev.map((r, i) => (i === rowIndex ? { ...r, added: true } : r))
    );
    setEphemeralRows((prev) => [
      ...prev,
      { id: generateId(), channel: "", identifier: "", added: false },
    ]);
  };

  const handleRemoveEndpoint = (rowIndex) => {
    const row = ephemeralRows[rowIndex];
    if (row.added) {
      setFormData((prev) => ({
        ...prev,
        endpoints: prev.endpoints.filter((ep) => ep.id !== row.id),
      }));
    }
    setEphemeralRows((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <RecordedContactInfoStep
            formData={formData}
            formErrors={formErrors}
            handleChange={handleChange}
            fields={fields}
            loadingUsers={loadingUsers}
            supervisorOptions={supervisorOptions}
          />
        );
      case 1:
        return (
          <RecordedContactEndpointStep
            ephemeralRows={ephemeralRows}
            formErrors={formErrors}
            handleEphemeralRowChange={handleEphemeralRowChange}
            handleAddEndpoint={handleAddEndpoint}
            handleRemoveEndpoint={handleRemoveEndpoint}
            channelOptions={channelOptions}
          />
        );
      case 2:
        return (
          <RecordedContactRetentionStep
            formData={formData}
            formErrors={formErrors}
            handleChange={handleChange}
            retentionPolicies={retentionPolicies}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {renderStepContent()}
      <Box display="flex" flexDirection="column" gap={2} mt={4}>
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack} fullWidth>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} fullWidth>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleFinalSubmit} fullWidth>
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default RecordedContactForm;
